/**
 * Probation Review & Confirmation — closes the loop on new joiners.
 *
 * Path: /app/probation  (HR roles)
 *
 * Pipeline:
 *   1. See queue of employees whose probation ends in next 30 days.
 *   2. Open an employee → record review (rating, strengths, recommendation).
 *   3. HR makes final decision: confirm / extend / terminate.
 *   4. On 'confirm', employment_type → permanent, confirmation_date set,
 *      welcome email sent. On 'extend', new probation_end_date assigned.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell, { SectionCard } from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Clock, CheckCircle, ArrowsClockwise, Warning, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const RATING_OPTIONS = [
  { v: "exceeds", l: "Exceeds expectations" },
  { v: "meets", l: "Meets expectations" },
  { v: "needs_improvement", l: "Needs improvement" },
  { v: "below", l: "Below expectations" },
];

export default function ProbationReview() {
  const [data, setData] = useState({ items: [], loading: true });
  const [open, setOpen] = useState(null); // employee detail

  const load = async () => {
    try {
      const r = await api.get("/probation/queue", { params: { window_days: 90, include_overdue: true } });
      setData({ ...r.data, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setData((d) => ({ ...d, loading: false }));
    }
  };
  useEffect(() => { load(); }, []);

  const overdue = data.items?.filter((e) => e.overdue) || [];
  const upcoming = data.items?.filter((e) => !e.overdue) || [];

  return (
    <AppShell title="Probation Review">
      <div className="max-w-6xl mx-auto" data-testid="probation-page">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900">Probation Review &amp; Confirmation</h1>
            <p className="text-sm text-zinc-600 mt-1">Convert probationary employees to permanent, extend, or initiate exit.</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="refresh-btn"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        </div>

        {data.loading ? (
          <Card className="p-6 text-sm text-zinc-500">Loading…</Card>
        ) : (
          <>
            {overdue.length > 0 && (
              <Card className="p-4 mb-4 border-red-300 bg-red-50" data-testid="overdue-banner">
                <div className="flex items-center gap-2 text-sm text-red-900">
                  <Warning size={18} weight="duotone" />
                  <strong>{overdue.length} employee{overdue.length === 1 ? "" : "s"} overdue</strong> — probation end date has passed without a decision.
                </div>
              </Card>
            )}

            <SectionCard title={`Employees in probation (${data.items?.length || 0})`}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Probation ends</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items?.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-zinc-500 italic py-6">
                      No probationary employees in the next 90 days. New joiners get their probation end date auto-set when added.
                    </TableCell></TableRow>
                  ) : data.items.map((e) => (
                    <TableRow key={e.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => setOpen(e)} data-testid={`probation-row-${e.id}`}>
                      <TableCell>
                        <div className="font-medium">{e.name}</div>
                        <div className="text-xs text-zinc-500">{e.email}</div>
                      </TableCell>
                      <TableCell className="text-sm">{e.job_title}</TableCell>
                      <TableCell className="text-xs text-zinc-500">{(e.joined_on || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-sm">{e.probation_end_date}</TableCell>
                      <TableCell>
                        {e.overdue ? <Badge variant="destructive">overdue</Badge>
                          : e.days_remaining <= 7 ? <Badge className="bg-amber-100 text-amber-900 border border-amber-300" variant="outline">due in {e.days_remaining}d</Badge>
                          : <span className="text-xs text-zinc-500">{e.days_remaining}d left</span>}
                      </TableCell>
                      <TableCell>
                        {e.review ? (
                          <Badge variant="outline" className="capitalize">recommend: {e.review.recommendation}</Badge>
                        ) : (
                          <span className="text-xs text-zinc-400 italic">no review yet</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={(ev) => { ev.stopPropagation(); setOpen(e); }} data-testid={`open-${e.id}`}>Review</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionCard>
          </>
        )}
      </div>

      <EmployeeDrawer
        employee={open}
        onClose={() => setOpen(null)}
        onSaved={() => { setOpen(null); load(); }}
      />
    </AppShell>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
function EmployeeDrawer({ employee, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [review, setReview] = useState({ rating: "meets", strengths: "", areas_to_improve: "", recommendation: "confirm", notes: "" });
  const [decisionDialog, setDecisionDialog] = useState(null); // {type: 'confirm'|'extend'|'terminate'}
  const [extendMonths, setExtendMonths] = useState(3);
  const [decisionNote, setDecisionNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!employee) return;
    (async () => {
      try {
        const r = await api.get(`/probation/${employee.id}`);
        setDetail(r.data);
        if (r.data.review) {
          setReview({
            rating: r.data.review.rating || "meets",
            strengths: r.data.review.strengths || "",
            areas_to_improve: r.data.review.areas_to_improve || "",
            recommendation: r.data.review.recommendation || "confirm",
            notes: r.data.review.notes || "",
          });
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [employee]);

  const saveReview = async () => {
    setSaving(true);
    try {
      await api.post(`/probation/${employee.id}/review`, review);
      toast.success("Review saved");
      const r = await api.get(`/probation/${employee.id}`);
      setDetail(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const decide = async () => {
    setSaving(true);
    try {
      const body = { decision: decisionDialog.type, note: decisionNote.trim() || null };
      if (decisionDialog.type === "extend") body.extend_months = extendMonths;
      await api.post(`/probation/${employee.id}/decide`, body);
      const verb = { confirm: "confirmed as permanent", extend: "extended", terminate: "routed to offboarding" }[decisionDialog.type];
      toast.success(`Employee ${verb}`);
      setDecisionDialog(null); setDecisionNote("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!employee) return null;
  const emp = detail?.employee || employee;
  const hasReview = !!detail?.review;

  return (
    <Sheet open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="!w-full sm:!max-w-3xl overflow-y-auto" data-testid="probation-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserCircle size={26} weight="duotone" className="text-zinc-700" />
            {emp.name}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <Card className="p-3 bg-zinc-50">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-xs text-zinc-500">Role:</span> {emp.job_title}</div>
              <div><span className="text-xs text-zinc-500">Email:</span> {emp.email}</div>
              <div><span className="text-xs text-zinc-500">Joined:</span> {(emp.joined_on || "").slice(0, 10)}</div>
              <div><span className="text-xs text-zinc-500">Probation ends:</span> <strong>{emp.probation_end_date || "—"}</strong></div>
              {emp.confirmation_date && <div className="col-span-2 text-emerald-700"><CheckCircle size={14} className="inline mr-1" /> Confirmed on {emp.confirmation_date}</div>}
            </div>
          </Card>

          {/* Review form */}
          <Card className="p-4">
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Clock size={16} weight="duotone" className="text-zinc-500" /> Manager review
              {hasReview && <Badge variant="outline" className="text-[10px]">submitted</Badge>}
            </h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Overall rating</Label>
                <Select value={review.rating} onValueChange={(v) => setReview({ ...review, rating: v })}>
                  <SelectTrigger data-testid="review-rating"><SelectValue /></SelectTrigger>
                  <SelectContent>{RATING_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Strengths</Label>
                <Textarea rows={3} value={review.strengths} onChange={(e) => setReview({ ...review, strengths: e.target.value })} data-testid="review-strengths" />
              </div>
              <div>
                <Label className="text-xs">Areas to improve</Label>
                <Textarea rows={3} value={review.areas_to_improve} onChange={(e) => setReview({ ...review, areas_to_improve: e.target.value })} data-testid="review-areas" />
              </div>
              <div>
                <Label className="text-xs">Recommendation</Label>
                <Select value={review.recommendation} onValueChange={(v) => setReview({ ...review, recommendation: v })}>
                  <SelectTrigger data-testid="review-recommendation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirm">Confirm as permanent</SelectItem>
                    <SelectItem value="extend">Extend probation</SelectItem>
                    <SelectItem value="terminate">Recommend termination</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={saveReview} disabled={saving} data-testid="save-review-btn">
                {saving ? "Saving…" : hasReview ? "Update review" : "Submit review"}
              </Button>
            </div>
          </Card>

          {/* HR decision panel */}
          <Card className="p-4 border-zinc-900 border-2">
            <h3 className="font-medium text-sm mb-2">HR decision</h3>
            <p className="text-xs text-zinc-500 mb-3">This action updates the employee&apos;s record and triggers downstream notifications.</p>
            <div className="grid grid-cols-3 gap-2">
              <Button onClick={() => setDecisionDialog({ type: "confirm" })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="decide-confirm-btn">
                <CheckCircle size={14} weight="bold" className="mr-1" /> Confirm
              </Button>
              <Button variant="outline" onClick={() => setDecisionDialog({ type: "extend" })} data-testid="decide-extend-btn">
                <Clock size={14} className="mr-1" /> Extend
              </Button>
              <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => setDecisionDialog({ type: "terminate" })} data-testid="decide-terminate-btn">
                <Warning size={14} className="mr-1" /> Terminate
              </Button>
            </div>
          </Card>

          {/* History */}
          {detail?.history?.length > 0 && (
            <Card className="p-3">
              <h3 className="font-medium text-sm mb-2">History</h3>
              <div className="space-y-1.5">
                {detail.history.map((h) => (
                  <div key={h.id} className="text-xs text-zinc-600">
                    <strong className="capitalize text-zinc-900">{h.decision}</strong> by {h.decided_by_name}
                    {h.extend_months && <> · extended by {h.extend_months} month(s) → {h.new_probation_end}</>}
                    {h.note && <> · &ldquo;{h.note}&rdquo;</>}
                    <span className="text-zinc-400 ml-1">· {(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </SheetContent>

      {/* Decision confirmation dialog */}
      <Dialog open={!!decisionDialog} onOpenChange={(o) => !o && setDecisionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionDialog?.type === "confirm" && "Confirm as permanent employee?"}
              {decisionDialog?.type === "extend" && "Extend probation"}
              {decisionDialog?.type === "terminate" && "Initiate termination"}
            </DialogTitle>
            <DialogDescription>
              {decisionDialog?.type === "confirm" && "This will set the employee's status to permanent, clear probation_end_date, and send a welcome confirmation email."}
              {decisionDialog?.type === "extend" && "Push the probation end date forward by a chosen number of months. The employee will be notified."}
              {decisionDialog?.type === "terminate" && "This routes the employee to the offboarding workflow. Their access remains until offboarding is processed."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {decisionDialog?.type === "extend" && (
              <div>
                <Label className="text-xs">Extend by (months)</Label>
                <Input type="number" min={1} max={12} value={extendMonths} onChange={(e) => setExtendMonths(Number(e.target.value) || 3)} data-testid="extend-months-input" />
              </div>
            )}
            <div>
              <Label className="text-xs">Note (shared with employee on confirm/extend)</Label>
              <Textarea rows={2} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} data-testid="decision-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(null)}>Cancel</Button>
            <Button onClick={decide} disabled={saving} data-testid="confirm-decision-btn">{saving ? "Saving…" : "Confirm decision"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
